import { createContext, useCallback, useContext, useRef, useState } from "react";
import { billingApi } from "@/api/billing";
import KeyPaymentGateway from "@/components/mist/KeyPaymentGateway";

/* ==========================================================================
   SITE-WIDE CREDIT INTERCEPT (approved plan: 1a server precheck · 3b drawer).
   Any component can gate a high-cost generation trigger:

     const { runGated } = useBillingGate();
     runGated("generate_image", () => fireExpensiveGeneration());

   Flow: POST /api/billing/precheck decides (bought credits / subscription /
   BYOK / founder). If allowed → the action runs immediately. Otherwise the
   KeyPaymentGateway drawer opens (FALL-CLOSED) and the pending action re-fires
   automatically once the user tops up or saves a BYOK key.
   ========================================================================== */
const Ctx = createContext(null);

export const useBillingGate = () =>
  useContext(Ctx) || { runGated: (_t, fn) => fn?.(), ensureAllowed: async () => true, openBilling: () => {} };

export function BillingGateProvider({ children }) {
  const [open, setOpen] = useState(false);
  const pendingRef = useRef(null);

  const ensureAllowed = useCallback(async (task = "overlay_image") => {
    try {
      const d = await billingApi.precheck(task);
      return !!d?.allowed;
    } catch {
      return false; // fall-closed: block on any precheck error
    }
  }, []);

  const runGated = useCallback(async (task, fn) => {
    if (await ensureAllowed(task)) return fn?.();
    pendingRef.current = fn;
    setOpen(true);
    return null;
  }, [ensureAllowed]);

  const close = useCallback(() => { pendingRef.current = null; setOpen(false); }, []);
  const onUnlocked = useCallback(() => {
    const fn = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    if (fn) { try { fn(); } catch { /* noop */ } }
  }, []);

  return (
    <Ctx.Provider value={{ runGated, ensureAllowed, openBilling: () => setOpen(true) }}>
      {children}
      <KeyPaymentGateway open={open} onClose={close} onUnlocked={onUnlocked} />
    </Ctx.Provider>
  );
}
